import { Component, ViewChild, Renderer2, ElementRef, AfterViewInit } from '@angular/core';
import { MatPaginator } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { SortTableAllFilteredData, SortTableFilteredDataNotFound, SortTableFilterObj } from '../../data-model/sort-table';

@Component({
    selector: 'bell-composite-table',
    templateUrl: './composite-table.component.html',
    styleUrls: ['./composite-table.component.scss'],
    standalone: false
})

export class CompositeTableComponent implements AfterViewInit {
  public sortTable: Element;
  public sortTableTitle: string;
  public sortTableData: Array<any> = [];
  public sortTableAllHref: Object = {};
  public sortTableAllHeaders: Object = {};
  public sortTableFilteredDataNotFound: SortTableFilteredDataNotFound = new SortTableFilteredDataNotFound;
  public sortTableFilterObj: Array<SortTableFilterObj> = [];
  public sortTableFilterValues: Object = {};
  public sortTableAllFilteredData: SortTableAllFilteredData = new SortTableAllFilteredData;

  public dataSource: MatTableDataSource<[]>;
  public displayedColumns: Array<string> = [];
  public displayedColumnsFilter: Array<string> = [];

  public taskDataModuleRef: Object = {};

  @ViewChild('sortTableElement', { static: true }) sortTableElement: ElementRef;
  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  constructor() {
  }

  ngAfterViewInit(): void {
    this.dataSource.paginator = this.paginator;
    this.dataSource.sort = this.sort;
    this.dataSource.filterPredicate = this.sortTableCreateFilter();
  }

  addSortTableElement(element: Element, renderer: Renderer2): boolean {
    this.sortTable = element.querySelector('#sortTable');

    if (!this.sortTable) {
      return false;
    }

    this.setSortingTable();

    renderer.appendChild(this.sortTable.parentElement, this.sortTableElement.nativeElement);
    renderer.removeChild(this.sortTable.parentElement, this.sortTable);

    // We are copying original XML DOC table width into new table.
    const colGroup = this.sortTable.querySelector('colgroup');
    const parentTable = this.sortTableElement.nativeElement.querySelector('.mat-table');
    if (colGroup && parentTable) {
      renderer.appendChild(parentTable, colGroup);
    }

    return true;
  }

  sortTableGeneralFilter(event: Event) {
    this.sortTableFilterValues = {};
    const generalFiltered = (event.target as HTMLInputElement).value.trim().toLowerCase();

    this.dataSource.data.forEach(
      (data) => {
        for (const key in data) {
          if (Object.prototype.hasOwnProperty.call(data, key)) {
            const value: string = data[key];

            if (value.toLowerCase().indexOf(generalFiltered) !== -1) {
              this.sortTableFilterValues[key] = generalFiltered;
              return;
            }
          }
        }
      }
    );

    this.dataSource.filter = JSON.stringify(this.sortTableFilterValues);
    this.sortTableFilterLastStep();
  }

  sortTableColumnFilter(filter: SortTableFilterObj, event: Event) {
    this.sortTableFilterValues[filter.columnProp] = (event.target as HTMLInputElement).value.trim().toLowerCase();
    this.dataSource.filter = JSON.stringify(this.sortTableFilterValues);

    this.sortTableFilterLastStep();
  }

  sortTableResetAllFilters() {
    const allColumnFilters = Array.from(document.querySelectorAll('.sortTable__input'));

    allColumnFilters.forEach(
      (el: HTMLInputElement) => {
        el.value = '';
      }
    );

    this.sortTableFilterValues = {};
    this.dataSource.filter = '';

    this.sortTableFilterLastStep();
  }

  sortTableResetFilter(filter: SortTableFilterObj, filterIndex: string) {
    (document.getElementById('sortTableColumnFilter_' + filterIndex) as HTMLInputElement).value = '';

    this.sortTableFilterValues[filter.columnProp] = '';
    this.dataSource.filter = JSON.stringify(this.sortTableFilterValues);

    this.sortTableFilterLastStep();
  }

  getHRefLink(colName: string): string {
    return this.sortTableAllHref[colName] ?? '';
  }

  private sortTableFilterLastStep() {
    if (this.dataSource.filteredData.length <= 0) {
      this.sortTableFilteredDataNotFound.notFound = true;
    } else {
      this.sortTableFilteredDataNotFound.notFound = false;
    }

    if (this.dataSource.paginator) {
      this.dataSource.paginator.firstPage();
    }
  }

  private setSortingTable() {
    this.sortTableTitle = this.sortTable.querySelector('caption').textContent;
    this.setSortTableHeaders();
    this.setSortTableData();
    this.dataSource = new MatTableDataSource(this.sortTableData);
    this.sortTableFilteredDataNotFound.colspan = this.displayedColumns.length;
  }

  private setSortTableHeaders() {
    this.sortTable.querySelector('thead').querySelector('tr').childNodes.forEach(
      (header) => {
        const h = header.textContent.toLocaleLowerCase()
          .replace(' ', '_')
          .replace('(', '')
          .replace(')', '')
          .replace('.', '')
          .replace('-', '');
        const hFilter = h + '-filter';

        this.displayedColumns.push(h);
        this.displayedColumnsFilter.push(hFilter);
        this.sortTableAllHeaders[h] = header.textContent;

        const filterSelectObj: SortTableFilterObj = new SortTableFilterObj;
        filterSelectObj.name = header.textContent;
        filterSelectObj.columnProp = h;

        this.sortTableFilterObj.push(filterSelectObj);
      }
    );
  }

  private setSortTableData() {
    const trData = [];

    this.sortTable.querySelector('tbody').querySelectorAll('tr').forEach((row) => {
      const rowData = [];
      row.childNodes.forEach((dataNode: Element) => {
        const hasAnchorTag = dataNode.getElementsByTagName('a');

        if (hasAnchorTag.length > 0) {
          const dataModuleRef = [];

          for (let anchor of hasAnchorTag) {
            dataModuleRef.push(anchor.textContent);
            this.sortTableAllHref[anchor.textContent] = anchor.getAttribute('href');
          }

          // Build Context Itemd for Data Module References.
          this.taskDataModuleRef[dataNode.textContent] = dataModuleRef;
          const tblHRef = this.sortTableAllHref[dataNode.textContent];
          if (tblHRef === null || tblHRef === undefined) {
            this.sortTableAllHref[dataNode.textContent] = [];
          }
        }

        rowData.push(dataNode.textContent);
      });

      trData.push(rowData);
    });

    trData.forEach((row) => {
      const rowData = {};
      for (let i = 0; i < row.length; i++) {
        const rd = row[i];

        rowData[this.displayedColumns[i]] = rd;
      }
      this.sortTableData.push(rowData);
    });

    const filteredOptions = this.sortTableFilterObj.filter((o) => {
      o.options = this.getSortTableFilterObj(this.sortTableData, o.columnProp);
      return o;
    });

    this.sortTableAllFilteredData.sortTableFilteredObjs = filteredOptions;
  }

  private getSortTableFilterObj(sortTableData: Array<any>, key: string): Array<string> {
    const uniqueOption = [];

    const filteredOptions = sortTableData.filter(
      (obj) => {
        if (!uniqueOption.includes(obj[key])) {
          uniqueOption.push(obj[key]);
        }

        return obj;
      }
    );

    this.sortTableAllFilteredData.sortTableData = filteredOptions;
    return uniqueOption;
  }

  private sortTableCreateFilter() {
    const filterFunction = function (data: any, filter: string): boolean {
      const searchTerms = JSON.parse(filter);
      let isFilterSet = false;

      for (const col in searchTerms) {
        if (searchTerms[col].toString() !== '') {
          isFilterSet = true;
        } else {
          delete searchTerms[col];
        }
      }

      const nameSearch = () => {
        const found = [];

        if (isFilterSet) {
          for (const col in searchTerms) {
            if (searchTerms) {
              searchTerms[col].trim().toLowerCase().split(' ').forEach(
                (word) => {
                  if (data[col].toString().toLowerCase().indexOf(word) !== -1 && isFilterSet) {
                    found.push(true);
                  } else {
                    found.push(false);
                  }
                }
              );
            }
          }
        }

        return found.every(Boolean);
      };

      return nameSearch();
    };

    return filterFunction;
  }
}
